import { Stack } from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import FaceliftDialog from '@/components/Atoms/FaceliftDialog';
import FaceliftText from '@/components/Atoms/FaceliftText';
import {
	ensureUserPresenceSessionId,
	updateUserPresence,
} from '@/services/users';
import { selectIsAuthenticated, useAuthStore } from '@/store';
import { useTourContext } from '@/store/tourContext';
import { trackCoreClick } from '@/utils/analytics';
import { INACTIVITY_TIMEOUT } from '@/utils';

const SESSION_REMINDER_LIMIT = 300 * 60 * 1000; // 60 minutes
const PRESENCE_HEARTBEAT_INTERVAL = 30 * 1000;
const INITIAL_PRESENCE_DEDUPE_WINDOW_MS = 5000;
const recentInitialPresenceSyncs = new Map<string, number>();

const ActivityMonitor = () => {
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const userId = useAuthStore((state) => state.user?.userInformation?._id || null);
  const logout = useAuthStore((state) => state.logout);
  const navigate = useNavigate();
  const tourContext = useTourContext();
  const isTourActive = tourContext?.isTourActive ?? false;

  const [showPrompt, setShowPrompt] = useState(false);
  const [countdown, setCountdown] = useState(30); // countdown in seconds
  const [modalType, setModalType] = useState<'inactivity' | 'session'>(
    'inactivity',
  );

  const timeoutId = useRef<NodeJS.Timeout | null>(null);
  const countdownInterval = useRef<NodeJS.Timeout | null>(null);
  const sessionReminderRef = useRef<NodeJS.Timeout | null>(null);
  const presenceHeartbeatRef = useRef<NodeJS.Timeout | null>(null);
  const isTourActiveRef = useRef<boolean>(false);
  const lastInteractionAtRef = useRef<number>(Date.now());
  const lastMouseMovementAtRef = useRef<number>(Date.now());

  // Refs for timer triggers
  const inactivityTimerTriggeredRef = useRef<boolean>(false);
  const sessionTimerTriggeredRef = useRef<boolean>(false);

  const clearExistingTimer = useCallback(() => {
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
      timeoutId.current = null;
    }
  }, []);

  const clearSessionReminderTimer = useCallback(() => {
    if (sessionReminderRef.current) {
      clearTimeout(sessionReminderRef.current);
      sessionReminderRef.current = null;
    }
  }, []);

  const clearCountdown = useCallback(() => {
    if (countdownInterval.current) {
      clearInterval(countdownInterval.current);
      countdownInterval.current = null;
    }
  }, []);

  const clearPresenceHeartbeat = useCallback(() => {
    if (presenceHeartbeatRef.current) {
      clearInterval(presenceHeartbeatRef.current);
      presenceHeartbeatRef.current = null;
    }
  }, []);

  const startInactivityTimer = useCallback(() => {
    clearExistingTimer();
    inactivityTimerTriggeredRef.current = false;

    timeoutId.current = setTimeout(
      () => {
        inactivityTimerTriggeredRef.current = true;
        if (!showPrompt && !isTourActiveRef.current) {
          setCountdown(30);
          setShowPrompt(true);
          setModalType('inactivity');
        }
      },
      INACTIVITY_TIMEOUT || 30 * 60 * 1000, // default 30 min
    );
  }, [clearExistingTimer]);

  const startSessionReminderTimer = useCallback(() => {
    clearSessionReminderTimer();
    sessionTimerTriggeredRef.current = false;

    sessionReminderRef.current = setTimeout(() => {
      sessionTimerTriggeredRef.current = true;
      if (!showPrompt && !isTourActiveRef.current) {
        setCountdown(30);
        setShowPrompt(true);
        setModalType('session');
      }
    }, SESSION_REMINDER_LIMIT);
  }, []);

  const syncPresence = useCallback(async () => {
    if (!userId) return;

    try {
      await updateUserPresence(userId, {
        sessionId: ensureUserPresenceSessionId(),
        lastInteractionAt: new Date(lastInteractionAtRef.current).toISOString(),
        lastMouseMovementAt: new Date(
          lastMouseMovementAtRef.current,
        ).toISOString(),
      });
    } catch (error) {
      console.error('Failed to sync user presence:', error);
    }
  }, [userId]);

  const handleActivity = useCallback((eventType?: string) => {
    const currentTime = Date.now();
    lastInteractionAtRef.current = currentTime;
    if (eventType === 'mousemove') {
      lastMouseMovementAtRef.current = currentTime;
    }

    // Only reset timer if prompt is not shown
    if (!showPrompt) startInactivityTimer();
  }, [showPrompt, startInactivityTimer]);

  const handleLogout = useCallback(async () => {
    clearExistingTimer();
    clearSessionReminderTimer();
    clearCountdown();
    setShowPrompt(false);
    // Await logout before navigation
    await logout();
    navigate('/login');
  }, [clearExistingTimer, clearSessionReminderTimer, clearCountdown, logout, navigate]);

  const keepLoggedIn = useCallback(() => {
    clearCountdown();
    setShowPrompt(false);
    startInactivityTimer();
    startSessionReminderTimer();
  }, [clearCountdown, startInactivityTimer, startSessionReminderTimer]);

  // Update tour active ref to track current state
  useEffect(() => {
    isTourActiveRef.current = isTourActive;
  }, [isTourActive]);

  // Restart timers when tour ends if they were triggered during tour
  useEffect(() => {
    if (!isTourActive && isAuthenticated && !showPrompt) {
      if (inactivityTimerTriggeredRef.current) {
        startInactivityTimer();
      }
      if (sessionTimerTriggeredRef.current) {
        startSessionReminderTimer();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isTourActive,
    isAuthenticated,
    showPrompt,
  ]);

  // Countdown logic when prompt is open
  useEffect(() => {
    if (!showPrompt) {
      clearCountdown();
      return;
    }

    // Start countdown
    setCountdown(30);
    countdownInterval.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearCountdown();
          handleLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearCountdown();
  }, [showPrompt, handleLogout, clearCountdown]);

  // Global activity listeners
  useEffect(() => {
    if (!isAuthenticated) return;
    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    const activityHandler = (event: Event) => handleActivity(event.type);
    startSessionReminderTimer();

    events.map((event) => window.addEventListener(event, activityHandler));
    startInactivityTimer();

    return () => {
      events.map((event) =>
        window.removeEventListener(event, activityHandler),
      );
      clearExistingTimer();
      clearSessionReminderTimer();
      clearCountdown();
    };
  }, [
    isAuthenticated,
    handleActivity,
    startInactivityTimer,
    startSessionReminderTimer,
    clearExistingTimer,
    clearSessionReminderTimer,
    clearCountdown,
  ]);

  useEffect(() => {
    if (!isAuthenticated || !userId) return;

    const currentTime = Date.now();
    lastInteractionAtRef.current = currentTime;
    lastMouseMovementAtRef.current = currentTime;

    const sessionId = ensureUserPresenceSessionId();
    const lastInitialSyncAt = recentInitialPresenceSyncs.get(sessionId) || 0;
    if (currentTime - lastInitialSyncAt > INITIAL_PRESENCE_DEDUPE_WINDOW_MS) {
      syncPresence().then(() => {
        recentInitialPresenceSyncs.set(sessionId, Date.now());
      });
    }
    clearPresenceHeartbeat();
    presenceHeartbeatRef.current = setInterval(() => {
      syncPresence();
    }, PRESENCE_HEARTBEAT_INTERVAL);

    return () => {
      clearPresenceHeartbeat();
    };
  }, [isAuthenticated, userId, syncPresence, clearPresenceHeartbeat]);

  return (
    <FaceliftDialog
      open={showPrompt}
      onOpenChange={(open) => {
        if (!open && showPrompt) return;
        setShowPrompt(open);
      }}
      title={modalType === 'inactivity' ? 'Warning: Inactivity Detected' : 'Session Reminder'}
      subtitle={modalType === 'inactivity' ? `You’ve been inactive. Stay signed in or log out now — you’ll be logged out automatically in ${countdown} seconds.` : `Your session will expire in ${countdown} seconds. You can stay signed in or log out now.`}
      sx={{ padding: 0, zIndex: 9 }}
      footer={
        <Stack direction="row">
          <FaceliftButton
            bg="brand.solid"
            fontWeight={700}
            onClick={keepLoggedIn}
          >
            Stay Signed In
          </FaceliftButton>
          <FaceliftButton
            bg="transparent"
            variant="outline"
            border="1px solid lightGray"
            onClick={() => {
              trackCoreClick('logout_clicked', {
                source_module: 'activity_monitor',
              });
              handleLogout();
            }}
          >
            <FaceliftText
              color={{ _dark: 'text.light', _light: 'text.dark' }}
              fontWeight={700}
            >
              Logout
            </FaceliftText>
          </FaceliftButton>
        </Stack>
      }
    />
  );
};

export default ActivityMonitor;
